import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth, EDITOR_ROLES } from "../context/AuthContext";
import { PageHeader } from "../components/Card";
import { DataTable, FilterSelect } from "../components/DataTable";
import { Severity, StatusPill, SEVERITIES as S } from "../components/Severity";

const REG = {
  risks: { title: "title", fields: { title: "text", severity: S, likelihood: "number", impact: "number", owner: "text", treatment: ["Mitigate", "Transfer", "Accept", "Avoid"], status: ["Open", "In Treatment", "Closed"] } },
  incidents: { title: "title", fields: { title: "text", severity: S, status: ["New", "Triage", "Contained", "Eradicated", "Closed"], soc_tier: ["L1", "L2", "L3"], mitre: "text" } },
  assets: { title: "name", fields: { name: "text", type: ["Server", "Application", "Network", "OT", "Cloud", "SaaS"], classification: ["Restricted", "Confidential", "Internal", "Public"], criticality: S, trust_zone: ["Internet", "DMZ", "Internal", "Restricted", "OT"], owner: "text" } },
  controls: { title: "name", fields: { name: "text", family: "text", framework: ["NIST 800-53", "ISO 27001", "NIST CSF 2.0", "QCB Tech Risk", "IEC 62443"], effectiveness: ["Effective", "Partially Effective", "Ineffective", "Not Tested"], last_tested: "date" } },
  changes: { title: "title", fields: { title: "text", type: ["Standard", "Normal", "Emergency"], risk: S, status: ["Draft", "CAB Review", "Approved", "Implemented", "Rolled Back"] } },
  services: { title: "name", fields: { name: "text", tier: ["Tier 1", "Tier 2", "Tier 3"], sla: "text", status: ["Operational", "Degraded", "Maintenance"] } },
  vendors: { title: "name", fields: { name: "text", criticality: S, risk_rating: S, assessment: ["Completed", "In Progress", "Overdue"], contract_end: "date" } },
  remediations: { title: "title", fields: { title: "text", severity: S, status: ["Open", "In Progress", "Verified", "Closed"], due_date: "date" } },
};
const human = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const RecordForm = ({ fields, value, onChange }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    {Object.entries(fields).map(([k, type]) => (
      <div key={k} className="space-y-1.5">
        <Label>{human(k)}</Label>
        {Array.isArray(type)
          ? <FilterSelect value={value[k] || ""} onChange={(v) => onChange({ ...value, [k]: v })} options={type} placeholder={human(k)} testId={`record-field-${k}`} allowAll={false} />
          : <Input data-testid={`record-field-${k}`} type={type} value={value[k] ?? ""} onChange={(e) => onChange({ ...value, [k]: type === "number" ? Number(e.target.value) : e.target.value })} className="rounded-xl" />}
      </div>
    ))}
  </div>
);

export default function Registers() {
  const { entity } = useParams();
  const { t } = useI18n();
  const { hasRole, isAdmin } = useAuth();
  const cfg = REG[entity] || REG.risks;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/registers/${entity}`, { params: { q } }).then((r) => setRows(r.data)), [entity, q]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);

  const save = async () => {
    try {
      if (edit.id) await api.patch(`/registers/${entity}/${edit.id}`, edit);
      else await api.post(`/registers/${entity}`, edit);
      toast.success(t("saved")); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (r) => { try { await api.delete(`/registers/${entity}/${r.id}`); toast.success(t("deleted")); load(); } catch (e) { toast.error(errMsg(e)); } };

  const cell = (v) => (S.includes(v) ? <Severity value={v} /> : typeof v === "string" && Object.values(cfg.fields).some((f) => Array.isArray(f) && f.includes(v)) ? <StatusPill value={v} /> : v);
  const cols = [
    { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs text-sky-700">{r.id}</span> },
    ...Object.keys(cfg.fields).map((k) => ({ key: k, label: human(k), render: (r) => (k === cfg.title ? <span className="font-medium text-slate-900">{r[k]}</span> : cell(r[k])) })),
    ...(hasRole(EDITOR_ROLES) ? [{ key: "act", label: t("actions"), render: (r) => (
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" data-testid={`edit-${r.id}`} onClick={() => setEdit({ ...r })} aria-label={t("edit")}><Pencil className="h-4 w-4" /></Button>
        {isAdmin && <Button size="icon" variant="ghost" data-testid={`delete-${r.id}`} onClick={() => remove(r)} aria-label={t("delete")}><Trash2 className="h-4 w-4 text-rose-700" /></Button>}
      </div>) }] : []),
  ];

  return (
    <div>
      <PageHeader title={t(`nav_${entity}`)} sub={`${t("total")}: ${rows.length}`}>
        {hasRole(EDITOR_ROLES) && <Button data-testid="new-record-button" onClick={() => setEdit({})} className="rounded-xl bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />{t("newRecord")}</Button>}
      </PageHeader>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute start-3 top-3 h-4 w-4 text-slate-400" />
        <Input data-testid="register-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="h-10 rounded-xl ps-9" />
      </div>
      <DataTable cols={cols} rows={rows} testId={`register-${entity}-table`} />
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="record-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? `${t("edit")} ${edit.id}` : t("newRecord")}</DialogTitle></DialogHeader>
          {edit && <RecordForm fields={cfg.fields} value={edit} onChange={setEdit} />}
          <DialogFooter><Button data-testid="record-save-button" onClick={save} className="rounded-xl bg-slate-900">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
